<script setup>
/*
    Display a value. Used to implement the text, numeric and label widgets.

    Used for Labels, Numeric.
    Features: Dynamic sizing, centered output, prefix & suffix text.

    The widget is composed of three optional parts: a prefix, a value, and a suffix.
    - primary: The main value display.
    - prefix: Text displayed before the value.
    - suffix: Text displayed after the value.

    Widget Properties:
    The widget's appearance and behavior are controlled by properties passed in the `widget` prop.
    These can include standard CSS properties like `background`, `color`, `font-size`, etc.

    Example Widget Configuration:
    {
        "style": {
            "background": "rgb(var(--v-theme-primary))",
            "color": "rgb(var(--v-theme-on-primary))",
            "font-size": "200px",
            "font-weight": "bold"
        },
        "format": "number",
        "data": {
            "primary": 100,
            "suffix": "msec"
        }
    }
*/
import {reactive, ref} from 'vue'
import {getWidgetProps} from '@/paks/vu-dash/BoardUtils'
import {formatValue} from '@/paks/js-format'

/* Expose the update method to be called by the parent dashboard container. */
defineExpose({update})

const props = defineProps({
    /* The widget configuration object that defines the appearance and data of the widget. */
    widget: Object,
})

const page = reactive({
    /* Dynamic arguments passed to the value span */
    args: {},
    /* Data to be displayed, including prefix, primary value, and suffix */
    data: {},
    /* The formatted value to be rendered in the template */
    value: null,
})

/* Template refs to the main component elements for dynamic styling. */
const primary = ref(null)
const prefix = ref(null)
const suffix = ref(null)
const value = ref(null)

/*
    Default styles for the widget parts.
    These are applied if not overridden by the widget's own style properties.
    The font sizes use container query units (cqw, cqh) to scale dynamically with the widget's size.
*/
const DefaultStyles = {
    prefix: {
        'font-size': 'max(10px, min(min(5cqw, 5cqh), 36px))',
    },
    primary: {
        'font-size': 'max(10px, min(min(18cqw, 18cqh), 144px))',
        'font-weight': 'bold',
        color: 'rgb(var(--v-theme-text-lighten-1))',
    },
    suffix: {
        'font-size': 'max(14px, min(min(7cqw, 7cqh), 36px))',
    },
    value: {
    },
}

/*
    The update function is called to refresh the widget's state and appearance.
    It is exposed to the parent component and is typically called when widget data changes.
*/
function update() {
    let widget = props.widget
    /*
        getWidgetProps extracts properties from the widget configuration.
        It separates styles, data, and other arguments.
        It also merges widget-specific styles with the default styles.
        The refs are passed to allow getWidgetProps to apply styles directly to the elements.
    */
    Object.assign(
        page,
        getWidgetProps(widget, {primary, prefix, suffix, value}, {styles: DefaultStyles})
    )
    /*
        Format the primary value for display using the specified format from the widget config.
        For example, it can format numbers with commas or apply other string transformations.
    */
    page.value = formatValue(page.data.primary, {format: widget.format})
}
</script>

<template>
    <vu-div ref="primary" class="value-widget">
        <div class="content">
            <span ref="prefix" class="prefix">{{ page.data.prefix }}</span>
            <span ref="value" class="value" v-bind="page.args">{{ page.value }}</span>
            <span ref="suffix" class="suffix">{{ page.data.suffix }}</span>
        </div>
    </vu-div>
</template>

<style lang="scss">
/*
    The 'value-widget' establishes a container context for container queries.
    This allows child elements to have styles that are responsive to the size of this widget,
    rather than the entire viewport.
    The height and width are set to 100% of the container's height (cqh) and width (cqw).
*/
.value-widget {
    container-type: size;
    position: relative;
    display: inline-grid;
    height: 100cqh !important;
    width: 100cqw !important;
    .content {
        display: flex;
        align-items: baseline;
        justify-content: center;
        align-self: center;
        white-space: nowrap;
    }
    .prefix {
        margin-right: 2px;
    }
    .suffix {
        margin-left: 2px;
    }
}
</style>
